import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Popover from '@radix-ui/react-popover';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import { parseDate } from 'helpers/dateParser';
import { inputCls } from 'modules/Common/form/v2/primitives/inputClasses';
import { usePopoverPortalContainer } from 'modules/Common/utils/overlayHost';
import { controlAnchorProps } from 'modules/Common/utils/gapAnchor';

// Radix-based calendar date picker on the app's v2 field language — themable
// (unlike the browser-chrome popup of <input type="date">) and consistent with
// CgFilterSelect/CgPopover, including the dialog-aware portal so it works
// inside modals.
//
// Contract (deliberately identical to a native date input, so it's a drop-in):
//   value    — 'YYYY-MM-DD' string or '' (the app's stored date format)
//   onChange — called with 'YYYY-MM-DD' (or '' when the field is cleared)
//
// The anchor is a real text input: users can still TYPE any custom date
// (parsed strictly via helpers/dateParser — same accepted formats as the
// upload flows), or pick from the calendar. Typing an incomplete value keeps
// a local draft; blur reverts unparseable drafts to the committed value.
//
// Display format is DD/MM/YYYY (the format users type across the app).

const DISPLAY_FORMAT = 'DD/MM/YYYY';
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
// Matches CgFilterDropdown's POPOVER_Z_INDEX so pickers layer like every
// other popover, inside dialogs included.
const POPOVER_Z_INDEX = 100050;

const toDisplay = (value) => (value ? dayjs(value).format(DISPLAY_FORMAT) : '');

// One calendar-header line: [<] label [>]. Used for both the year and the
// month row so each is independently steppable.
const StepperRow = ({ label, onPrev, onNext, prevLabel, nextLabel }) => (
  <div className="flex items-center justify-between">
    <button
      type="button"
      aria-label={prevLabel}
      onClick={onPrev}
      className="w-7 h-7 rounded-lg text-slate-500 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40"
    >
      <FontAwesomeIcon
        icon={faChevronLeft}
        className="text-xs"
      />
    </button>
    <span className="text-sm font-semibold text-slate-800">{label}</span>
    <button
      type="button"
      aria-label={nextLabel}
      onClick={onNext}
      className="w-7 h-7 rounded-lg text-slate-500 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40"
    >
      <FontAwesomeIcon
        icon={faChevronRight}
        className="text-xs"
      />
    </button>
  </div>
);

export default function CgDatePicker({
  value = '',
  onChange,
  disabled = false,
  error = false,
  id,
  placeholder = DISPLAY_FORMAT,
  className = '',
  anchor,
}) {
  const { t } = useTranslation('common');
  // Was dialog-only; now covers drawers and legacy `.modal` hosts too, on the
  // shared grammar. Unchanged where it actually renders today (both live call
  // sites are inside a CgDialog), which is the point — this is plumbing.
  const [wrapperRef, portalContainer] = usePopoverPortalContainer();
  const [open, setOpen] = useState(false);
  // Draft holds in-progress typing; null means "mirror the committed value".
  const [draft, setDraft] = useState(null);
  // First-of-month the calendar shows; seeded from the value when opened.
  const [viewMonth, setViewMonth] = useState(() =>
    (value ? dayjs(value) : dayjs()).startOf('month'),
  );

  const selected = value ? dayjs(value) : null;
  const today = dayjs();

  const openCalendar = () => {
    if (disabled) return;
    setViewMonth((selected || today).startOf('month'));
    setOpen(true);
  };

  const commit = (d) => {
    onChange?.(d ? d.format('YYYY-MM-DD') : '');
  };

  const handleInputChange = (text) => {
    setDraft(text);
    if (text.trim() === '') {
      commit(null);
      return;
    }
    const parsed = parseDate(text);
    if (parsed) commit(parsed);
  };

  const handleInputBlur = () => setDraft(null);

  const draftUnreadable = draft !== null && draft.trim() !== '' && !parseDate(draft);

  // 6-week grid starting on the Monday of the view month's first week.
  const gridDays = useMemo(() => {
    const first = viewMonth.startOf('month');
    const mondayOffset = (first.day() + 6) % 7; // dayjs: Sunday = 0
    const gridStart = first.subtract(mondayOffset, 'day');
    return Array.from({ length: 42 }, (_, i) => gridStart.add(i, 'day'));
  }, [viewMonth]);

  const dayClasses = (d) => {
    const isSelected = selected && d.isSame(selected, 'day');
    const isToday = d.isSame(today, 'day');
    const inMonth = d.isSame(viewMonth, 'month');
    if (isSelected) return 'bg-primaryColor text-white font-semibold';
    return [
      inMonth ? 'text-slate-700' : 'text-slate-300',
      isToday ? 'ring-1 ring-inset ring-primaryColor/50 font-semibold' : '',
      'hover:bg-slate-100',
    ].join(' ');
  };

  return (
    <Popover.Root
      open={open}
      onOpenChange={setOpen}
    >
      <div
        ref={wrapperRef}
        className={`relative ${className}`}
      >
        <input
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder={placeholder}
          disabled={disabled}
          value={draft ?? toDisplay(value)}
          onChange={(e) => handleInputChange(e.target.value)}
          onBlur={handleInputBlur}
          aria-invalid={draftUnreadable || undefined}
          {...controlAnchorProps(anchor, !value)}
          className={inputCls(error || draftUnreadable, 'pr-9')}
        />
        <Popover.Trigger asChild>
          <button
            type="button"
            aria-label={t('common:datePicker.openCalendar', 'Open calendar')}
            disabled={disabled}
            onClick={openCalendar}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-primaryColor disabled:text-slate-300 disabled:cursor-not-allowed transition-colors p-1"
          >
            <FontAwesomeIcon
              icon={faCalendarAlt}
              className="text-sm"
            />
          </button>
        </Popover.Trigger>
      </div>
      <Popover.Portal container={portalContainer ?? undefined}>
        <Popover.Content
          align="start"
          sideOffset={4}
          // Keep the fixed-width panel inside the viewport on small screens:
          // Radix flips/shifts on collision, and the padding stops it hugging
          // the screen edge (same treatment as CgFilterDropdown).
          collisionPadding={8}
          style={{ zIndex: POPOVER_Z_INDEX }}
          className="bg-white rounded-xl shadow-lg border border-slate-200 p-3 w-[264px]"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {/* Year and month each on their own stepper line so jumping across
              months/years is a single click either way. */}
          <div className="space-y-1 mb-2">
            <StepperRow
              label={viewMonth.format('YYYY')}
              onPrev={() => setViewMonth((m) => m.subtract(1, 'year'))}
              onNext={() => setViewMonth((m) => m.add(1, 'year'))}
              prevLabel={t('common:datePicker.previousYear', 'Previous year')}
              nextLabel={t('common:datePicker.nextYear', 'Next year')}
            />
            <StepperRow
              label={viewMonth.format('MMMM')}
              onPrev={() => setViewMonth((m) => m.subtract(1, 'month'))}
              onNext={() => setViewMonth((m) => m.add(1, 'month'))}
              prevLabel={t('common:datePicker.previousMonth', 'Previous month')}
              nextLabel={t('common:datePicker.nextMonth', 'Next month')}
            />
          </div>
          <div className="grid grid-cols-7 mb-1">
            {WEEKDAYS.map((w) => (
              <span
                key={w}
                className="text-[10px] font-bold uppercase text-slate-400 text-center py-1"
              >
                {w}
              </span>
            ))}
          </div>
          <div
            className="grid grid-cols-7 gap-0.5"
            role="grid"
            aria-label={t('common:datePicker.calendar', 'Calendar')}
          >
            {gridDays.map((d) => (
              <button
                key={d.format('YYYY-MM-DD')}
                type="button"
                aria-label={d.format('D MMMM YYYY')}
                aria-pressed={selected ? d.isSame(selected, 'day') : false}
                onClick={() => {
                  commit(d);
                  setDraft(null);
                  setOpen(false);
                }}
                className={`h-8 rounded-lg text-xs tabular-nums transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40 ${dayClasses(d)}`}
              >
                {d.date()}
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={() => {
                commit(today);
                setDraft(null);
                setViewMonth(today.startOf('month'));
                setOpen(false);
              }}
              className="text-[11px] font-semibold text-primaryColor hover:underline"
            >
              {t('common:datePicker.today', 'Today')}
            </button>
            {value && (
              <button
                type="button"
                onClick={() => {
                  commit(null);
                  setDraft(null);
                }}
                className="text-[11px] font-medium text-slate-400 hover:text-slate-600"
              >
                {t('common:actions.clear', 'Clear')}
              </button>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
